import React, { useEffect, useRef, useState } from 'react'
import { Select, Form, Input, Label } from 'semantic-ui-react'
import { append, countryOptions, preventDefault } from '../helpers'
import ProductButton from './product_button'
import PhoneInput from './phone_input'

export const defaultAddress = {
	name: '',
	contactName: '',
	address1: '',
	address2: '',
	city: '',
	postalCode: '',
	state: '',
	note: '',
	reference: '',
	countryCode: '',
	countryName: '',
	email: '',
	mobile: '',
}

export function formatAddress(address = defaultAddress, separator = ', ') {
	const parts = [
		address.address1,
		address.address2,
		[address.postalCode, address.city].filter(item => typeof item === 'string').map(item => item.trim()).filter(item => item.length > 0).join(' '),
		address.countryName
	]
	return parts.filter(item => typeof item === 'string').map(item => item.trim()).filter(item => item.length > 0).join(separator)
}

const fallback = (value, fallbackValue = '') => typeof value !== 'string' ? fallbackValue : value

export default function AddressInput({
	compact,
	address,
	cityLoading,
	onChange,
}) {
	compact = compact ?? false
	address = address ?? defaultAddress
	cityLoading = cityLoading ?? false
	onChange = onChange ?? ((address, changedField) => {})

	const lastAddress = useRef({...defaultAddress})
	const [compactShowAddressInput, setCompactShowAddressInput] = useState(false)

	const nameEmpty = typeof address.name !== 'string' || address.name.trim().length === 0
	const nameTooLong = !nameEmpty && address.name.length > 35
	const referenceEmpty = typeof address.reference !== 'string' || address.reference.trim().length === 0
	const referenceTooLong = !referenceEmpty && address.reference.length > 35
	const noteEmpty = typeof address.note !== 'string' || address.note.trim().length === 0
	const noteTooLong = !noteEmpty && address.note.length > 35
	const contactNameEmpty = typeof address.contactName !== 'string' || address.contactName.trim().length === 0
	const contactNameTooLong = !contactNameEmpty && address.contactName.length > 35
	const address1Empty = typeof address.address1 !== 'string' || address.address1.trim().length === 0
	const address1TooLong = !address1Empty && address.address1.length > 35
	const address2Empty = typeof address.address2 !== 'string' || address.address2.trim().length === 0
	const address2TooLong = !address2Empty && address.address2.length > 35
	const postalCodeEmpty = typeof address.postalCode !== 'string' || address.postalCode.trim().length === 0
	const postalCodeTooLong = !postalCodeEmpty && address.postalCode.length > 35
	const cityEmpty = typeof address.city !== 'string' || address.city.trim().length === 0
	const cityTooLong = !cityEmpty && address.city.length > 35
	const mobileEmpty = typeof address.mobile !== 'string' || address.mobile.trim().length === 0
	const mobileTooLong = !mobileEmpty && address.mobile.length > 35
	const emailEmpty = typeof address.email !== 'string' || address.email.trim().length === 0
	const emailTooLong = !emailEmpty && address.email.length > 35
	const mobileAndEmailEmpty = mobileEmpty && emailEmpty
	const hasErrors = nameEmpty || nameTooLong || contactNameTooLong ||
		address1Empty || address1TooLong || address2TooLong ||
		postalCodeEmpty || postalCodeTooLong ||
		cityEmpty || cityTooLong ||
		mobileAndEmailEmpty || mobileTooLong || emailTooLong ||
		referenceTooLong || noteTooLong

	useEffect(() => {
		if (compact && !cityLoading && hasErrors) {
			setCompactShowAddressInput(true)
		} else {
			let changedFields = 0;
			for (let key in address) {
				if (address[key] !== lastAddress.current[key]) {
					changedFields++;
				}
			}
			if (changedFields > 1) {
				setCompactShowAddressInput(false)
			}
		}
		lastAddress.current = {...address}
	}, [compact, address, cityLoading, hasErrors])

	let elems = []

	if (compact) {
		elems.push(<ProductButton
			key="toggleAddressInput"
			labelPosition="top"
			label={compactShowAddressInput ? "Trykk for å slutte å endre adressen" : "Trykk for å endre adressen"}
			active={compactShowAddressInput}
			onClick={preventDefault(() => setCompactShowAddressInput(hasErrors ? true : !compactShowAddressInput))}
		>
			{!compactShowAddressInput ? <p>
				{!nameEmpty ? address.name : ''}<br/>
				{formatAddress(address)}<br/>
				{!contactNameEmpty || !mobileEmpty || !emailEmpty || !referenceEmpty || !noteEmpty ? <br/> : null}
				{!contactNameEmpty ? <>Kontaktperson: {address.contactName}<br/></> : null}
				{!mobileEmpty ? <>Telefonnummer: {address.mobile}<br/></> : null}
				{!emailEmpty ? <>E-post: {address.email}<br/></> : null}
				{!referenceEmpty ? <>Referanse: {address.reference}<br/></> : null}
				{!noteEmpty ? <>Notat: {address.note}<br/></> : null}
			</p> : <p>...</p> }
		</ProductButton>)
		elems.push(<p key="addressInputSpacer"></p>)
	}

	if (!compact || compactShowAddressInput) elems.push(<React.Fragment key="addressInputForm">
		<Form.Group widths="equal">
			<Form.Field required error={nameEmpty || nameTooLong}>
				<label>Navn</label>
				<Input
					type="text"
					placeholder="Eksempelfirma AS"
					value={fallback(address.name, '')}
					maxLength="35"
					onChange={(e, data) => {
						const name = data.value
						onChange(append(address, { name }), 'name')
					}}
				/>
				{nameEmpty || nameTooLong ? <Label prompt pointing="above">Navn må være mellom 1 og 35 tegn</Label> : null}
			</Form.Field>
			<Form.Field error={contactNameTooLong}>
				<label>Kontaktperson</label>
				<Input
					type="text"
					placeholder="Ola Nordmann"
					value={fallback(address.contactName, '')}
					maxLength="35"
					onChange={(e, data) => {
						const contactName = data.value
						onChange(append(address, { contactName }), 'contactName')
					}}
				/>
				{contactNameTooLong ? <Label prompt pointing="above">Kontaktperson kan være maks 35 tegn</Label> : null}
			</Form.Field>
		</Form.Group>
		<Form.Group widths="equal">
			<Form.Field required error={address1Empty || address1TooLong}>
				<label>Adresse (linje 1)</label>
				<Input
					type="text"
					placeholder="Eksempelveien 1"
					maxLength="35"
					value={fallback(address.address1, '')}
					onChange={(e, data) => {
						const address1 = data.value
						onChange(append(address, { address1 }), 'address1')
					}}
				/>
			</Form.Field>
			<Form.Field error={address2TooLong}>
				<label>Adresse (linje 2)</label>
				<Input
					type="text"
					placeholder="For eksempel bruksnummer (H0101)"
					maxLength="35"
					value={fallback(address.address2, '')}
					onChange={(e, data) => {
						const address2 = data.value
						onChange(append(address, { address2 }), 'address2')
					}}
				/>
				{address2TooLong ? <Label prompt pointing="above">Adresse (linje 2) kan være maks 35 tegn</Label> : null}
			</Form.Field>
		</Form.Group>
		<Form.Group>
			<Form.Field width={3} required error={postalCodeEmpty || postalCodeTooLong}>
				<label>Postnummer</label>
				<Input
					type="text"
					placeholder="0000"
					maxLength="35"
					value={fallback(address.postalCode, '')}
					onChange={(e, data) => {
						const postalCode = data.value
						onChange(append(address, { postalCode }), 'postalCode')
					}}
				/>
				{postalCodeEmpty || postalCodeTooLong ? <Label prompt pointing="above">Postnummer må være mellom 1 og 35 tegn</Label> : null}
			</Form.Field>
			<Form.Field width={13} required error={cityEmpty || cityTooLong}>
				<label>Poststed</label>
				<Input
					type="text"
					placeholder="Eksempelbyen"
					maxLength="35"
					loading={cityLoading}
					disabled={cityLoading}
					value={fallback(address.city, '')}
					onChange={(e, data) => {
						const city = data.value
						onChange(append(address, { city }), 'city')
					}}
				/>
				{cityEmpty || cityTooLong ? <Label prompt pointing="above">Poststed må være mellom 1 og 35 tegn</Label> : null}
			</Form.Field>
		</Form.Group>
		<Form.Field required>
			<label>Land</label>
			<Select
				search
				options={countryOptions.no}
				value={fallback(address.countryCode, '')}
				onChange={(_, data) => {
					const option = data.options.find(option => option.value === data.value)
					if (!option) return
					const countryCode = option.value
					const countryName = option.text
					onChange(append(address, { countryCode, countryName }), 'countryCode')
				}}
			/>
		</Form.Field>
		<div><Form.Field label="Mobil og/eller e-post" required /></div>
		<Form.Group widths="equal">
			<Form.Field error={mobileAndEmailEmpty || mobileTooLong}>
				<label><small>Telefonnummer</small></label>
				<PhoneInput
					value={fallback(address.mobile, '')}
					onChange={mobile => {
						onChange(append(address, { mobile }), 'mobile')
					}}
				/>
				{mobileAndEmailEmpty ? <Label prompt pointing="above">Telefonnummer eller e-post er påkrevd</Label> : null}
				{mobileTooLong ? <Label prompt pointing="above">Telefonnummer kan være maks 35 tegn</Label> : null}
			</Form.Field>
			<Form.Field error={mobileAndEmailEmpty || emailTooLong}>
				<label><small>E-post</small></label>
				<Input
					type="email"
					placeholder="ola.nordmann@example.com"
					maxLength="35"
					value={fallback(address.email, '')}
					onChange={(e, data) => {
						const email = data.value
						onChange(append(address, { email }), 'email')
					}}
				/>
				{emailTooLong ? <Label prompt pointing="above">E-post kan være maks 35 tegn</Label> : null}
			</Form.Field>
		</Form.Group>
		<Form.Group widths="equal">
			<Form.Field error={referenceTooLong}>
				<label>Referanse</label>
				<Input
					placeholder="Oppgi en referanse"
					maxLength="35"
					value={fallback(address.reference, '')}
					onChange={(e, data) => {
						const reference = data.value
						onChange(append(address, { reference }), 'reference')
					}}
				/>
				{referenceTooLong ? <Label prompt pointing="above">Referanse kan være maks 35 tegn</Label> : null}
			</Form.Field>
			<Form.Field error={noteTooLong}>
				<label>Notat</label>
				<Input
					placeholder="Oppgi ekstra informasjon om adressen, hvis nødvendig"
					maxLength="35"
					value={fallback(address.note, '')}
					onChange={(e, data) => {
						const note = data.value
						onChange(append(address, { note }), 'note')
					}}
				/>
				{noteTooLong ? <Label prompt pointing="above">Notat kan være maks 35 tegn</Label> : null}
			</Form.Field>
		</Form.Group>
	</React.Fragment>)

	return elems
}